import { useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ISODate, TaskId } from '@interfaces/IScheduler';
import type { ITaskSpan } from '@apis/schedule';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { DateField } from '@components/ui/DateField/DateField';
import { localDateToYmd, ymdToLocalDate } from '@components/Pages/AnalyticsPage/utils/dateRules';
import { ChevronDownIcon } from '@utils/icons';
import { useSchedulerPortalContainer } from '../../context/SchedulerPortalContainer';
import type { IFinishQuestion } from '../../hooks/useTaskFinish';
import type { CascadeFreeze } from '../../lib/freeze';
import { finishDayBounds, finishSpans, planFinish, type IFinishMove, type IFinishPlan } from '../../lib/finishPlan';
import { formatDay } from '../../lib/taskDates';
import { daysBetween } from '../../lib/timeScale';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './FinishTaskDialog.module.css';

// ---------------------------------------------------------------------------
// FinishTaskDialog — what Mark done asks when it is not the planned end day
// (docs/specs/2026-09-22-done-on-the-day-amendment.md).
//
//   Early — the end moves to the finish day; pulling the tasks pinned behind
//           it earlier is offered, off by default (crews have to be free).
//   Late  — on time, or late on a day; a late finish moves the end, and
//           pushing what follows is offered, on by default (declining leaves
//           their links visibly broken).
//
// Top to bottom: the question (late only), the two dates side by side with the
// variance between them, then what the answer does to the tasks that follow —
// computed live from the server's copy of the plan with the Gantt's own
// cascade, so the numbers the manager reads are the ones that get written.
// ---------------------------------------------------------------------------

type LateAnswer = 'onTime' | 'late';

interface IFinishTaskDialogProps {
  question: IFinishQuestion;
  /** Observed-work anchors: the cascade flows around them, and an observed
   *  start is the finish day's floor. */
  frozen: ReadonlyMap<TaskId, CascadeFreeze>;
  pending: boolean;
  /** The spans to write, or `null` when no date moves (finished on time). */
  onConfirm: (spans: ITaskSpan[] | null) => void;
  onClose: () => void;
}

const DAY = 'yyyy/MM/dd';
const SHORT_DAY = 'MM/dd';

export function FinishTaskDialog({ question, frozen, pending, onConfirm, onClose }: IFinishTaskDialogProps) {
  const { t } = useTranslation();
  const answerGroup = useId();
  const container = useSchedulerPortalContainer();
  const { timing, today, base, task } = question;
  const [finishedOn, setFinishedOn] = useState(today);
  const [answer, setAnswer] = useState<LateAnswer | null>(null);
  const [withMoves, setWithMoves] = useState(timing === 'late');

  const bounds = finishDayBounds(timing, task, today, frozen.has(task.id));
  const moving = timing === 'early' || answer === 'late';
  const plan = useMemo(
    () => (moving ? planFinish(base.tasks, base.dependencies, frozen, task, finishedOn) : null),
    [moving, base, frozen, task, finishedOn],
  );
  const planned = formatDay(task.end, DAY);
  // The day the ledger reports: the picked one while a date is being chosen,
  // the planned one for "on time", nothing until a late task's question is
  // answered.
  const actual = moving ? finishedOn : answer === 'onTime' ? task.end : null;

  const actualValue = moving ? (
    <DateField
      value={ymdToLocalDate(finishedOn)}
      // Tz-naive: the picker's local calendar day IS the picked day, and it
      // disables every day outside the bounds, so what it reports is valid.
      onChange={(next) => setFinishedOn(localDateToYmd(next))}
      placeholder={t('scheduler.finish.finishedOn')}
      ariaLabel={t('scheduler.finish.finishedOn')}
      {...(bounds.min ? { minDate: ymdToLocalDate(bounds.min) } : {})}
      maxDate={ymdToLocalDate(bounds.max)}
      disabled={pending}
      elevated
      container={container}
    />
  ) : (
    <span className={styles.value}>{actual ? planned : '—'}</span>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      container={container}
      title={t('scheduler.finish.title')}
      description={task.name}
      footer={
        <>
          <span className={styles.spacer} />
          <Button variant="secondary" size="sm" onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            onClick={() => onConfirm(plan ? finishSpans(plan, withMoves) : null)}
            disabled={pending || (timing === 'late' && answer === null)}
            aria-busy={pending}
            data-testid="finish-confirm"
          >
            {t('scheduler.panel.actions.markDone')}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        {timing === 'late' && (
          <fieldset className={styles.question} disabled={pending}>
            <legend className={styles.prompt}>{t('scheduler.finish.latePrompt')}</legend>
            <div className={styles.options}>
              <Option name={answerGroup} checked={answer === 'onTime'} onSelect={() => setAnswer('onTime')} hint={t('scheduler.finish.onTimeHint')}>
                {t('scheduler.finish.onTime')}
              </Option>
              <Option name={answerGroup} checked={answer === 'late'} onSelect={() => setAnswer('late')} hint={t('scheduler.finish.lateHint')}>
                {t('scheduler.finish.late')}
              </Option>
            </div>
          </fieldset>
        )}
        <dl className={styles.ledger} aria-label={t('scheduler.finish.dateSummary')}>
          <div className={styles.cell}>
            <dt>{t('scheduler.finish.plannedFinish')}</dt>
            <dd><span className={styles.value}>{planned}</span></dd>
          </div>
          <div className={styles.cell}>
            <dt>{t('scheduler.finish.actualFinish')}</dt>
            <dd>{actualValue}</dd>
            {actual && <dd><Variance days={daysBetween(task.end, actual)} /></dd>}
          </div>
        </dl>
        {plan && <Cascade plan={plan} timing={timing} withMoves={withMoves} onToggle={setWithMoves} disabled={pending} />}
      </div>
    </Modal>
  );
}

interface IOptionProps {
  name: string;
  checked: boolean;
  onSelect: () => void;
  hint: string;
  children: ReactNode;
}

/** One answer to the late question, as a selectable card. */
function Option({ name, checked, onSelect, hint, children }: IOptionProps) {
  return (
    <label className={styles.option} data-checked={checked || undefined}>
      <input type="radio" name={name} checked={checked} onChange={onSelect} />
      <span className={styles.optionText}>
        <strong>{children}</strong>
        <small>{hint}</small>
      </span>
    </label>
  );
}

/** How far the actual finish is from the planned one, in the ladder's tones. */
function Variance({ days }: { days: number }) {
  const { t } = useTranslation();
  if (days === 0) return <span className={styles.variance} data-tone="onTime">{t('scheduler.finish.onTime')}</span>;
  const early = days < 0;
  return (
    <span className={styles.variance} data-tone={early ? 'early' : 'late'}>
      {t(early ? 'scheduler.finish.daysEarly' : 'scheduler.finish.daysLate', { count: Math.abs(days) })}
    </span>
  );
}

interface ICascadeProps {
  plan: IFinishPlan;
  timing: IFinishQuestion['timing'];
  withMoves: boolean;
  onToggle: (next: boolean) => void;
  disabled: boolean;
}

/** The offer to move what follows — or the line saying nothing has to. */
function Cascade({ plan, timing, withMoves, onToggle, disabled }: ICascadeProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (plan.moves.length === 0) return <p className={styles.quiet}>{t('scheduler.finish.nothingMoves')}</p>;
  const label = t(timing === 'early' ? 'scheduler.finish.pull' : 'scheduler.finish.push', { count: plan.moves.length });
  return (
    <section className={styles.impact}>
      <label className={styles.impactToggle}>
        <Checkbox checked={withMoves} onCheckedChange={onToggle} ariaLabel={label} disabled={disabled} />
        <span className={styles.impactText}>
          <span>{label}</span>
          <small>{t('scheduler.finish.upTo', { span: t('scheduler.finish.days', { count: plan.maxShiftDays }) })}</small>
        </span>
      </label>
      {timing === 'late' && !withMoves && <p className={styles.declined}>{t('scheduler.finish.pushDeclined')}</p>}
      <button type="button" className={styles.disclosure} onClick={() => setOpen((shown) => !shown)} aria-expanded={open}>
        <span className={styles.caret} data-collapsed={!open} aria-hidden>
          <ChevronDownIcon size={14} />
        </span>
        {t(open ? 'scheduler.finish.hideTasks' : 'scheduler.finish.showTasks')}
      </button>
      {open && (
        <ScrollArea className={styles.moves}>
          <ul className={styles.moveList}>
            {plan.moves.map((move) => <MoveRow key={move.taskId} move={move} />)}
          </ul>
        </ScrollArea>
      )}
    </section>
  );
}

const shortSpan = (span: { start: ISODate; end: ISODate }) =>
  span.start === span.end ? formatDay(span.start, SHORT_DAY) : `${formatDay(span.start, SHORT_DAY)}–${formatDay(span.end, SHORT_DAY)}`;

/** One dependent task: its name in full, its dates before and after, and by
 *  how much its end moves. */
function MoveRow({ move }: { move: IFinishMove }) {
  const { t } = useTranslation();
  const shift = daysBetween(move.from.end, move.to.end);
  return (
    <li className={styles.move}>
      <span className={styles.moveName}>{move.name}</span>
      <span className={styles.moveDates}>
        {shortSpan(move.from)} → {shortSpan(move.to)}
      </span>
      <span className={styles.moveShift}>
        {shift < 0 ? '−' : '+'}
        {t('scheduler.finish.days', { count: Math.abs(shift) })}
      </span>
    </li>
  );
}
